/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { AsyncSelect } from 'chakra-react-select';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { INfseEmitente, INfseEmpresaBusca } from '../../../../../types/nfseEmissao';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

interface Opcao {
  value: number;
  label: string;
  empresa: INfseEmpresaBusca;
}

interface EmpresaBuscaProps {
  // Empresa escolhida que ainda não emite (sem certificado). Tem prioridade na exibição.
  empresa?: INfseEmpresaBusca | null;
  // Emitente escolhido; o rótulo sai da lista de emitentes já carregada.
  emitenteId?: number | null;
  emitentes: INfseEmitente[];
  onSelecionar: (empresa: INfseEmpresaBusca | null) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  inputId?: string;
}

function rotulo(codigo: string | null, razao: string, cnpj: string | null) {
  return [codigo, razao, cnpj ? formatCNPJ(cnpj) : null].filter(Boolean).join(' · ');
}

function opcao(empresa: INfseEmpresaBusca): Opcao {
  return {
    value: empresa.empresa_id,
    label: rotulo(empresa.external_id, empresa.razao_social, empresa.cnpj),
    empresa,
  };
}

// Busca de empresa por código, CNPJ ou nome, habilitada ou não. Escolher não habilita
// nada: a empresa passa a emitir quando o certificado digital dela é enviado.
export function EmpresaBusca({
  empresa,
  emitenteId,
  emitentes,
  onSelecionar,
  isDisabled,
  isInvalid,
  inputId,
}: EmpresaBuscaProps) {
  const { buscarEmpresas } = useNfseEmissao();

  const carregar = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (texto: string): Promise<Opcao[]> =>
      new Promise(resolve => {
        clearTimeout(timer);
        if (!texto.trim()) {
          resolve([]);
          return;
        }
        timer = setTimeout(() => {
          buscarEmpresas(texto)
            .then(lista => resolve(lista.map(opcao)))
            .catch(() => resolve([]));
        }, 300);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atual = emitentes.find(e => e.id === emitenteId);
  let valor: Opcao | null = null;
  if (empresa) valor = opcao(empresa);
  else if (atual) {
    valor = opcao({
      empresa_id: atual.empresa_id,
      external_id: '',
      razao_social: atual.razao_social,
      cnpj: atual.cnpj,
      municipio: atual.municipio,
      uf: atual.uf,
      emitente_id: atual.id,
    });
  }

  return (
    <AsyncSelect<Opcao>
      inputId={inputId}
      placeholder="Digite o código, o CNPJ ou o nome da empresa"
      loadOptions={carregar}
      value={valor}
      onChange={escolhida => onSelecionar((escolhida as Opcao | null)?.empresa ?? null)}
      formatOptionLabel={(item, { context }) =>
        context === 'menu' ? (
          <Box>
            <Text fontSize="13px">{item.label}</Text>
            <Text fontSize="11px" color={item.empresa.emitente_id ? 'gray.500' : 'orange.500'}>
              {[item.empresa.municipio, item.empresa.uf].filter(Boolean).join('/') || '—'}
              {item.empresa.emitente_id ? '' : ' · sem certificado digital'}
            </Text>
          </Box>
        ) : (
          item.label
        )
      }
      loadingMessage={() => 'Buscando...'}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? 'Nenhuma empresa encontrada' : 'Digite para buscar'
      }
      isClearable
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
      {...propsMenuSelect}
    />
  );
}
